/** @jsxRuntime classic */
/** @jsx jsx */
import { css, jsx } from "@emotion/react";
import CheckCircleIcon from "@material-ui/icons/CheckCircle";
import firebase from "firebase/app";
import { useI18n } from "hooks/i18n/i18n";
import { useState } from "react";

import { PhoneNumberVerificationForm } from "./PhoneNumberVerificationForm";
import { formatPhoneNumber, maskPhoneNumber } from "./formatPhoneNumber";

import "firebase/auth";

import type { FC } from "react";

type Props = {
  /**
   * 描画先は呼び出し側が用意し、表示が切り替わっても消えない位置に置くこと。
   * 再描画で要素ごと消えると、チャレンジ中の reCAPTCHA が壊れる。
   */
  recaptchaContainerId: string;
  description: string;
  verifiedNote?: string;
};

export const PhoneNumberVerification: FC<Props> = ({
  recaptchaContainerId,
  description,
  verifiedNote,
}) => {
  // 認証直後は firebase.User が同一参照のまま更新されるため、
  // store ではなく currentUser を見る。再描画のきっかけは verifiedAt で作る
  const [verifiedAt, setVerifiedAt] = useState(0);
  const [isChangingPhoneNumber, setIsChangingPhoneNumber] = useState(false);
  const registeredPhoneNumber =
    firebase.auth().currentUser?.phoneNumber ?? null;

  if (registeredPhoneNumber !== null && !isChangingPhoneNumber) {
    return (
      <VerifiedPhoneNumberCard
        key={verifiedAt}
        registeredPhoneNumber={registeredPhoneNumber}
        verifiedNote={verifiedNote}
        onClickChangeNumber={() => setIsChangingPhoneNumber(true)}
      />
    );
  }

  return (
    <PhoneNumberVerificationForm
      recaptchaContainerId={recaptchaContainerId}
      description={description}
      currentPhoneNumber={registeredPhoneNumber ?? undefined}
      onCancel={() => setIsChangingPhoneNumber(false)}
      onVerified={() => {
        setIsChangingPhoneNumber(false);
        setVerifiedAt(Date.now());
      }}
    />
  );
};

type VerifiedPhoneNumberCardProps = {
  registeredPhoneNumber: string;
  verifiedNote?: string;
  onClickChangeNumber: React.MouseEventHandler<HTMLButtonElement>;
};

const VerifiedPhoneNumberCard: FC<VerifiedPhoneNumberCardProps> = ({
  registeredPhoneNumber,
  verifiedNote,
  onClickChangeNumber,
}) => {
  const { t } = useI18n();
  const classes = styles();

  return (
    <div css={classes.card}>
      <div css={classes.left}>
        <p css={classes.status}>
          <CheckCircleIcon css={classes.statusIcon} />
          <span>{t("phoneNumber.statusVerified")}</span>
        </p>
        <div css={classes.registered}>
          <p css={classes.registeredLabel}>{t("phoneNumber.registered")}</p>
          <p css={classes.registeredNumber}>
            {maskPhoneNumber(formatPhoneNumber(registeredPhoneNumber))}
          </p>
        </div>
      </div>
      {verifiedNote && <p css={classes.note}>{verifiedNote}</p>}
      <button
        type="button"
        onClick={onClickChangeNumber}
        css={classes.changeButton}
      >
        {t("phoneNumber.changeNumber")}
      </button>
    </div>
  );
};

const styles = () => {
  return {
    card: css({
      display: "grid",
      gap: "16px",
      width: "100%",
      maxWidth: "740px",
      margin: "0 auto",
      padding: "16px",
      backgroundColor: "#eaf7f5",
      border: "2px solid #57bfae",
      "@media screen and (min-width: 768px)": {
        gap: "20px",
        padding: "24px",
        borderWidth: "1px",
        gridTemplateColumns: "1fr auto",
        gridTemplateAreas: '"left button" "note note"',
      },
    }),
    left: css({
      display: "flex",
      flexDirection: "column",
      gap: "12px",
      minWidth: 0,
      "@media screen and (min-width: 768px)": {
        gridArea: "left",
      },
    }),
    status: css({
      display: "flex",
      gap: "10px",
      alignItems: "center",
      margin: 0,
      fontSize: "16px",
      fontWeight: "bold",
      lineHeight: "22px",
      "@media screen and (min-width: 768px)": {
        fontSize: "18px",
        lineHeight: "25px",
      },
    }),
    statusIcon: css({
      flex: "none",
      fontSize: "24px",
      color: "#00c2ae",
    }),
    registered: css({
      display: "flex",
      flexDirection: "column",
      gap: "2px",
    }),
    registeredLabel: css({
      margin: 0,
      color: "#6f6f6f",
      fontSize: "12px",
      lineHeight: "18px",
      "@media screen and (min-width: 768px)": {
        fontSize: "13px",
        lineHeight: "20px",
      },
    }),
    registeredNumber: css({
      margin: 0,
      fontSize: "20px",
      fontWeight: "bold",
      lineHeight: "28px",
      "@media screen and (min-width: 768px)": {
        fontSize: "22px",
        lineHeight: "30px",
      },
    }),
    note: css({
      margin: 0,
      color: "#6f6f6f",
      fontSize: "12px",
      lineHeight: "18px",
      "@media screen and (min-width: 768px)": {
        gridArea: "note",
        fontSize: "13px",
        lineHeight: "20px",
      },
    }),
    changeButton: css({
      width: "100%",
      minHeight: "44px",
      padding: "10px 16px",
      color: "#0b0b0b",
      fontFamily: "inherit",
      fontSize: "14px",
      fontWeight: "bold",
      lineHeight: "20px",
      backgroundColor: "#fff",
      border: "1px solid #0b0b0b",
      cursor: "pointer",
      "@media screen and (min-width: 768px)": {
        gridArea: "button",
        justifySelf: "end",
        alignSelf: "center",
        // 文言の長さに合わせる。固定幅にすると英語で折り返す
        width: "auto",
        minWidth: "180px",
        minHeight: 0,
        whiteSpace: "nowrap",
      },
    }),
  };
};
